import type { DocumentMeta } from '../core/types';
import type { FullSnapshot } from '../engine/historyOps';

/** In-canvas Type tool editing session. Kept out of React so tools can drive it. */

export interface TextEditSession {
  layerId: string;
  /** True when the layer was created for this session and should be dropped if empty. */
  isNew: boolean;
  docId: string;
  metaBefore: DocumentMeta;
  surfaceBefore: FullSnapshot | null;
  /** Blur right after open is ignored — mouseup after the placing click would cancel it. */
  openedAt: number;
}

let session: TextEditSession | null = null;
const listeners = new Set<() => void>();

/** How long after opening an edit session blur-to-commit is suppressed. */
export const TEXT_EDIT_BLUR_GRACE_MS = 400;

function notify(): void {
  for (const listener of listeners) listener();
}

export function getTextEditSession(): TextEditSession | null {
  return session;
}

export function subscribeTextEdit(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function beginTextEditSession(
  layerId: string,
  isNew: boolean,
  docId: string,
  metaBefore: DocumentMeta,
  surfaceBefore: FullSnapshot | null,
): void {
  session = {
    layerId,
    isNew,
    docId,
    metaBefore,
    surfaceBefore,
    openedAt: performance.now(),
  };
  notify();
}

export function endTextEditSession(): void {
  if (!session) return;
  session = null;
  notify();
}

export function isTextEditBlurSuppressed(): boolean {
  if (!session) return false;
  return performance.now() - session.openedAt < TEXT_EDIT_BLUR_GRACE_MS;
}
